import { SHIPPER_SCOPED_OVERRIDE_TYPES } from './modals/constants';

export const MARKETPLACE_KEY = 'ONWARD_MARKETPLACE';

// A contractor is excluded even when it also carries CARRIER — it is listed from its partnerships instead.
export const isOrgFlexCenter = (client) => {
    const roles = client?.user?.roles || {};
    return !!roles['CARRIER'] && !roles['SHIPPER'] && !roles['CONTRACTOR'];
};

// Split carriers into assetHeavy and assetLight differentiators
export const buildCarrierAssetMaps = ({
    integrationOverrides = [],
    pricingOverrides = [],
    adminClients = [],
    partnerships = [],
} = {}) => {
    const orgClientIds = new Set((adminClients || []).map((c) => c.client_id));
    const clientsMap = Object.fromEntries((adminClients || []).map((c) => [c.client_id, c]));

    // Role detection prefers roles embedded on the pricing payload (so external
    // carriers — those NOT in admin_clients — still resolve correctly), falling
    // back to clientsMap (admin_clients) for any side without an embedded record.
    const rolesFor = (client_id, embedded) => {
        const fromEmbedded = embedded?.user?.roles;
        if (fromEmbedded) return fromEmbedded;
        return clientsMap[client_id]?.user?.roles || {};
    };
    const isShipperOf = (client_id, embedded) => !!rolesFor(client_id, embedded)?.['SHIPPER'];
    const isCarrierOf = (client_id, embedded) => {
        const roles = rolesFor(client_id, embedded);
        return !!roles?.['CARRIER'] && !roles?.['SHIPPER'];
    };

    const assetHeavy = {};
    const assetLight = {};
    const defaultOverridesByCarrier = {};
    const flexCenterPricingByContractor = {};

    // Always surface the marketplace row in Network Carriers, even with no overrides.
    assetLight[MARKETPLACE_KEY] = { carrier: null };

    // Seed every org carrier so Flex Centers shows up even with no pricing/integration overrides.
    for (const client of adminClients || []) {
        if (isOrgFlexCenter(client)) {
            assetHeavy[client.client_id] ??= { carrier: client };
        }
    }

    const getMap = (carrier_id) => (orgClientIds.has(carrier_id) ? assetHeavy : assetLight);

    for (const override of integrationOverrides || []) {
        const { type, carrier_id, middle_mile_carrier_id, shipper_id, carrier, middle_mile_carrier } = override;
        // Shipper-scoped types (e.g. PC Miler mileage) have their own surface and are carrier-less;
        // don't bucket them as marketplace integrations or count them in the integrations card.
        if (SHIPPER_SCOPED_OVERRIDE_TYPES.includes(type)) continue;
        // Integrations not bound to a carrier belong to the marketplace (null) row.
        // The shipper still gets a section under MARKETPLACE_KEY so manual marketplace
        // connections surface; non-MANUAL types accumulate as configured integrations.
        const carrierId = carrier_id || middle_mile_carrier_id;
        const carrierObj = carrier || middle_mile_carrier;

        if (!carrierId) {
            assetLight[MARKETPLACE_KEY] ??= { carrier: null };
            if (shipper_id) {
                assetLight[MARKETPLACE_KEY][shipper_id] ??= {
                    integrations: [],
                    carrierPricing: [],
                    shipperPricing: [],
                };
                assetLight[MARKETPLACE_KEY][shipper_id].integrations.push(override);
            }
            continue;
        }
        const map = getMap(carrierId);
        map[carrierId] ??= { carrier: carrierObj };
        map[carrierId][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
        // MANUAL rows are surfaced as "needs setup" inside the integrations modal;
        // Carriers/utils dispatchIntegrationsOf leaves them out of the TMS tags.
        map[carrierId][shipper_id].integrations.push(override);
    }

    for (const p of pricingOverrides || []) {
        const isOrgDefault = p.organization_id && !p.partner_client_id;
        // Every other key on a carrier's entry is a shipper, so an org default can't live there.
        if (isOrgDefault) {
            (defaultOverridesByCarrier[p.client_id] ??= []).push(p);
            continue;
        }

        if (p.override_type === 'CONTRACTOR') {
            flexCenterPricingByContractor[p.client_id] ??= {};
            (flexCenterPricingByContractor[p.client_id][p.partner_client_id] ??= []).push(p);
            continue;
        }

        const clientIsShipper = isShipperOf(p.client_id, p.client);
        const clientIsCarrier = isCarrierOf(p.client_id, p.client);
        const partnerIsShipper = isShipperOf(p.partner_client_id, p.partner_client);
        const partnerIsCarrier = isCarrierOf(p.partner_client_id, p.partner_client);

        // Branch 1: carrier (client_id) → shipper (partner_client_id)
        if (clientIsCarrier && partnerIsShipper) {
            const carrier_id = p.client_id;
            const shipper_id = p.partner_client_id;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].carrierPricing.push(p);
        }
        // Branch 2: shipper (client_id) → carrier (partner_client_id)
        else if (clientIsShipper && partnerIsCarrier) {
            const shipper_id = p.client_id;
            const carrier_id = p.partner_client_id;
            if (!carrier_id) continue;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.partner_client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].shipperPricing.push(p);
        }
        // Branch 3: shipper-side rate with no carrier counterpart on partner_client_id.
        // - assign_id set → "Rate Shop": pre-assigned to a specific carrier; drops into
        //   that carrier's shipperPricing.
        // - assign_id null → "Auto/Manual": marketplace; drops into the MARKETPLACE_KEY row.
        else if (clientIsShipper && !p.partner_client_id) {
            const shipper_id = p.client_id;
            if (p.assign_id) {
                const carrier_id = p.assign_id;
                const map = getMap(carrier_id);
                map[carrier_id] ??= { carrier: p.assign || null };
                map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
                map[carrier_id][shipper_id].shipperPricing.push(p);
            } else {
                assetLight[MARKETPLACE_KEY] ??= { carrier: null };
                assetLight[MARKETPLACE_KEY][shipper_id] ??= {
                    integrations: [],
                    carrierPricing: [],
                    shipperPricing: [],
                };
                assetLight[MARKETPLACE_KEY][shipper_id].shipperPricing.push(p);
            }
        }
        // Fallback: when only one side resolves to a known role.
        // Common case: org shipper on one side, external carrier on the other (no admin_clients entry, no embedded user/roles).
        // Treat the unknown side as the carrier so external carriers still surface.
        else if (clientIsShipper && !partnerIsShipper && p.partner_client_id) {
            const shipper_id = p.client_id;
            const carrier_id = p.partner_client_id;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.partner_client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].shipperPricing.push(p);
        } else if (partnerIsShipper && !clientIsShipper && p.client_id) {
            const carrier_id = p.client_id;
            const shipper_id = p.partner_client_id;
            const map = getMap(carrier_id);
            map[carrier_id] ??= { carrier: p.client };
            map[carrier_id][shipper_id] ??= { integrations: [], carrierPricing: [], shipperPricing: [] };
            map[carrier_id][shipper_id].carrierPricing.push(p);
        }
    }

    // A partnership carries no settings of its own, so it only adds a pair no override or pricing row already has.
    for (const partnership of partnerships) {
        const map = getMap(partnership.carrier_id);
        map[partnership.carrier_id] ??= { carrier: partnership.carrier };
        map[partnership.carrier_id][partnership.shipper_id] ??= {
            integrations: [],
            carrierPricing: [],
            shipperPricing: [],
        };
    }

    return { assetHeavy, assetLight, defaultOverridesByCarrier, flexCenterPricingByContractor };
};

/**
 * Index delivery profiles by the shipper each one auto-assigns. A shipper can be on several profiles, so each
 * entry is a list with one item per profile: its name and the carrier ids it maps anywhere in its subregions.
 * @param {Array} deliveryProfiles rows from GET_ORG_PROFILE_ASSIGNMENTS
 * @returns {Object} keyed by shipper_id -> [{ profileName, mappedCarrierIds: Set }]
 */
export const buildAssignmentsByShipper = (deliveryProfiles) => {
    const result = {};
    (deliveryProfiles || []).forEach((profile) => {
        const mappedCarrierIds = new Set();
        (profile.subregions || []).forEach((s) => {
            (s.carriers || []).forEach((m) => {
                if (m.carrier_id) mappedCarrierIds.add(m.carrier_id);
                if (m.middle_mile_carrier_id) mappedCarrierIds.add(m.middle_mile_carrier_id);
                if (m.warehouse_carrier_id) mappedCarrierIds.add(m.warehouse_carrier_id);
            });
        });
        (profile.auto_assignments || []).forEach(({ shipper_id }) => {
            (result[shipper_id] ??= []).push({ profileName: profile.name, mappedCarrierIds });
        });
    });
    return result;
};

/**
 * One shipper's profiles collapsed for display: the distinct profile names and the union of their carriers. Used by
 * the corporate Shippers table and the shipper detail page's Overview.
 * @param {Array} assignments one shipper's entry from buildAssignmentsByShipper
 * @returns {?Object} { profileNames, profileName, mappedCarrierIds: Set }, or null for no profiles.
 *   `profileName` is the names joined with ", " for callers that show a single label.
 */
export const summarizeAssignments = (assignments) => {
    if (!assignments?.length) return null;
    const profileNames = [...new Set(assignments.map((a) => a.profileName))];
    const mappedCarrierIds = new Set(assignments.flatMap((a) => [...a.mappedCarrierIds]));
    return { profileNames, profileName: profileNames.join(', '), mappedCarrierIds };
};

export const buildContractorRows = (partnerships = []) => {
    const byContractor = new Map();

    for (const partnership of partnerships || []) {
        const contractor = partnership?.contractor;
        const contractorId = partnership?.contractor_id || contractor?.client_id;
        if (!contractorId) continue;

        if (!byContractor.has(contractorId)) {
            byContractor.set(contractorId, {
                client_id: contractorId,
                contractor: contractor || null,
                flexCenters: [],
            });
        }

        const row = byContractor.get(contractorId);
        const flexCenterId = partnership?.carrier_id;
        if (flexCenterId && !row.flexCenters.some((f) => f.client_id === flexCenterId)) {
            row.flexCenters.push({
                client_id: flexCenterId,
                business_name: partnership?.carrier?.business_name || null,
                partnership_id: partnership?.partnership_id || null,
                status: partnership?.status || null,
            });
        }
    }

    const byName = (a, b) => (a || '').localeCompare(b || '');

    return [...byContractor.values()]
        .map((row) => ({
            ...row,
            flexCenters: [...row.flexCenters].sort((a, b) => byName(a.business_name, b.business_name)),
        }))
        .sort((a, b) => byName(a.contractor?.business_name, b.contractor?.business_name));
};

export const initialsFor = (name) => {
    const trimmedName = (name || '').trim();
    if (!trimmedName) return '';

    const words = trimmedName.split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * Whether a client with these `user.roles` can be the carrier side of a shipper↔carrier partnership. Unknown roles
 * can't.
 * @param {Object} [roles]
 * @returns {boolean}
 */
export const isPartnershipCarrier = (roles) => !!roles?.['CARRIER'] && !roles?.['SHIPPER'] && !roles?.['CONTRACTOR'];

/**
 * The `partnerships` rows to insert with a Network Carriers connection, so the carrier lists the shipper as a shipping
 * partner. Pass the result as the override inserts' `partnerships` variable.
 * @param {Object} args
 * @param {Object} args.shipper - The shipper client: `client_id`, `business_name`, `test_acc`, `user.roles`.
 * @param {Object|null} args.carrier - The carrier client with the same fields, or null for marketplace.
 * @returns {Object[]} Zero or one `partnerships_insert_input`.
 */
export const connectionPartnerships = ({ shipper, carrier }) => {
    if (!shipper?.client_id || !carrier?.client_id || carrier.client_id === shipper.client_id) return [];
    if (!shipper.user?.roles?.['SHIPPER'] || !isPartnershipCarrier(carrier.user?.roles)) return [];
    if (!!shipper.test_acc !== !!carrier.test_acc) return [];

    return [
        {
            shipper_id: shipper.client_id,
            carrier_id: carrier.client_id,
            status: 'APPROVED',
            shipper_alias: shipper.business_name || null,
        },
    ];
};
